import { ReactNode } from "react";
import { Info } from "lucide-react";

import { Popover } from "@/design-system/components/Popover/Popover";
import { WebTrackerRiskLevel } from "./types";

type RiskLevelDefinition = {
  level: WebTrackerRiskLevel;
  label: string;
  dotClass: string;
  definition: string;
};

const RISK_LEVELS: RiskLevelDefinition[] = [
  {
    level: "highest",
    label: "Highest",
    dotClass: "bg-red-700",
    definition:
      "Shares PHI, the vendor doesn't offer BAAs, and Freshpaint offers a HIPAA-compliant replacement.",
  },
  {
    level: "high",
    label: "High",
    dotClass: "bg-red-500",
    definition: "Shares (or may share) PHI and the vendor doesn't offer BAAs.",
  },
  {
    level: "medium",
    label: "Medium",
    dotClass: "bg-yellow-500",
    definition:
      "Shares PHI, but the vendor supports signing BAAs (e.g. they publicly agree to, or a customer has one).",
  },
  {
    level: "low",
    label: "Low",
    dotClass: "bg-green-700",
    definition:
      "Doesn't share PHI based on our research, or effectively requires a BAA to be used safely.",
  },
  {
    level: "unknown",
    label: "Unknown",
    dotClass: "bg-gray-400",
    definition:
      "Detected and recognized, but not yet researched enough to assign a rating.",
  },
];

export function RiskLevelInfoContent({
  currentLevel,
}: {
  currentLevel?: WebTrackerRiskLevel;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">How we assign risk</h3>
        <p className="text-textMediumEmphasis text-sm">
          Risk reflects whether a tracker shares (or could share) PHI and
          whether the vendor supports BAAs. We research each tracker at a point
          in time using a clean browser, inspecting its network requests and
          cookies for a health-context signal (page/URL content, media, map
          locations, or form fields) combined with an identifier.
        </p>
        <p className="text-textMediumEmphasis text-sm">
          Behavior can change after our last review and we don&apos;t observe
          every visit in real time, so we err conservative — and you can mark a
          tracker as safe based on your own context.
        </p>
      </div>

      <ul className="flex flex-col gap-2">
        {RISK_LEVELS.map((level) => {
          const isCurrent = level.level === currentLevel;
          return (
            <li
              key={level.label}
              className={`flex items-start gap-3 rounded-md p-2 ${
                isCurrent ? "bg-blue-50 ring-1 ring-blue-200" : ""
              }`}
            >
              <span
                className={`mt-1 h-3 w-3 shrink-0 rounded-full ${level.dotClass}`}
                aria-hidden="true"
              />
              <div className="flex flex-col">
                <span className="flex items-center gap-2 text-sm font-medium">
                  {level.label}
                  {isCurrent && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                      This tracker
                    </span>
                  )}
                </span>
                <span className="text-textMediumEmphasis text-sm">
                  {level.definition}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function RiskLevelInfo({
  currentLevel,
  trigger,
}: {
  currentLevel?: WebTrackerRiskLevel;
  trigger?: ReactNode;
}) {
  return (
    <Popover
      tracking="wtm-risk-level-info"
      showClose
      contentProps={{
        align: "start",
        className: "max-w-md max-h-[70vh] overflow-auto",
      }}
      trigger={
        trigger ?? (
          <span className="text-textMediumEmphasis hover:text-textDefault inline-flex items-center gap-1 text-sm">
            <Info size={16} />
            How risk levels work
          </span>
        )
      }
    >
      <RiskLevelInfoContent currentLevel={currentLevel} />
    </Popover>
  );
}
